import React, { useEffect } from 'react'
import { AppLink as Link } from '@components/AppLink'
import { observer } from 'mobx-react-lite'
import { IconSprite } from '@app/landing/IconSprite'
import { Icon } from '@app/landing/Icon'
import { FxBackground } from '@app/landing/FxBackground'
import { SectionLink } from '@app/landing/sectionNav'
import { applyFxLiteClass, removeFxLiteClass } from '@utils/fxLite'
import { LandingHeader } from '@app/landing/LandingHeader'
import { PublicFooter } from '@app/landing/PublicFooter'
import { useStore } from '@store/StoreProvider'
import { SUPPORT_BOT_HANDLE, SUPPORT_BOT_URL, supportBotLabel } from '@constants/support'
import '@app/landing/landing.css'
import './notFound.css'

const NotFoundIconSprite: React.FC = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <symbol id="nf-book" viewBox="0 0 24 24">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </symbol>
    <symbol id="nf-pulse" viewBox="0 0 24 24">
      <path d="M3 12h4l2.5-7 5 14L17 12h4" />
    </symbol>
  </svg>
)

const NotFoundPage: React.FC = observer(() => {
  const { i18n } = useStore()

  const t = (key: string, fallback: string) => {
    const value = i18n.t(key)
    return value === key ? fallback : value
  }

  useEffect(() => {
    document.title = t('errors.page.not_found_title', 'Страница не найдена — ChatX')
    document.documentElement.classList.add('landing-active')
    applyFxLiteClass()
    window.scrollTo(0, 0)
    return () => {
      document.documentElement.classList.remove('landing-active')
      removeFxLiteClass()
    }
  }, [i18n.language, i18n.translations])

  return (
    <div className="landing-page not-found-page font-sans antialiased overflow-x-hidden relative min-h-screen flex flex-col">
      <IconSprite />
      <NotFoundIconSprite />
      <FxBackground />
      <LandingHeader />

      <main className="nf-main relative flex-1">
        <section className="nf-hero">
          <div className="nf-gridbg" />
          <div className="nf-in">
            <div className="nf-num" aria-label="404">
              <i>4</i>
              <span className="nf-mark" aria-hidden="true" />
              <i>4</i>
            </div>

            <h1 className="nf-title">
              {t('errors.page.not_found', 'Такой страницы не нашлось')}
            </h1>
            <p className="nf-sub">
              {t(
                'errors.page.not_found_description',
                'Возможно, ссылка устарела, страницу перенесли или в адресе опечатка. Всё остальное на месте и работает.',
              )}
            </p>

            <div className="nf-acts">
              <Link to="/" className="nf-btn nf-btn-primary">
                <Icon id="ic-arrow-right" className="ic ic-sm" />
                {t('nav.to_home', 'На главную')}
              </Link>
              <Link to="/knowledge-base" className="nf-btn nf-btn-ghost">
                <Icon id="nf-book" className="ic ic-sm" />
                {t('nav.knowledge_base', 'База знаний')}
              </Link>
            </div>
          </div>
        </section>

        <section className="nf-chat">
          <div className="nf-chat-head">
            <span />
            <b>{t('errors.page.searched', 'Мы поискали')}</b>
            <span />
          </div>

          <div className="nf-bubble">
            <span className="nf-av" aria-hidden="true" />
            <div className="nf-msg">
              {t(
                'errors.page.searched_message',
                'Такой страницы у нас нет. Зато есть эти — возможно, вы искали что-то из списка.',
              )}
            </div>
          </div>

          <div className="nf-links">
            <SectionLink section="pricing" className="nf-link">
              <span className="nf-ib">
                <Icon id="ic-card" className="ic ic-sm" />
              </span>
              <span>
                <b>{t('errors.page.link_pricing', 'Тарифы и оплата')}</b>
                <span>{t('errors.page.link_pricing_desc', 'Три тарифа, подписки на операторов')}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm nf-go" />
            </SectionLink>
            <Link to="/knowledge-base" className="nf-link">
              <span className="nf-ib">
                <Icon id="nf-book" className="ic ic-sm" />
              </span>
              <span>
                <b>{t('nav.knowledge_base', 'База знаний')}</b>
                <span>{t('errors.page.link_kb_desc', 'Подключение, настройка, биллинг')}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm nf-go" />
            </Link>
            <Link to="/status" className="nf-link">
              <span className="nf-ib">
                <Icon id="nf-pulse" className="ic ic-sm" />
              </span>
              <span>
                <b>{t('errors.page.link_status', 'Статус сервиса')}</b>
                <span>{t('errors.page.link_status_desc', 'Состояние компонентов в реальном времени')}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm nf-go" />
            </Link>
            <Link to="/register" className="nf-link">
              <span className="nf-ib">
                <Icon id="ic-message" className="ic ic-sm" />
              </span>
              <span>
                <b>{t('errors.page.link_try', 'Попробовать ChatX')}</b>
                <span>{t('errors.page.link_try_desc', '7 дней бесплатно, карта не нужна')}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm nf-go" />
            </Link>
            <a href={SUPPORT_BOT_URL} target="_blank" rel="noopener noreferrer" className="nf-link">
              <span className="nf-ib">
                <Icon id="ic-send" className="ic ic-sm" />
              </span>
              <span>
                <b>{supportBotLabel((k) => i18n.t(k), i18n.language)}</b>
                <span>{SUPPORT_BOT_HANDLE}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm nf-go" />
            </a>
          </div>

          <p className="nf-note">
            {t('errors.page.broken_link_prefix', 'Пришли по ссылке из письма или из чата и попали сюда?')}{' '}
            <button type="button" data-chatx-open>
              {t('errors.page.write_us', 'Напишите нам')}
            </button>
            {' '}
            {t('errors.page.broken_link_suffix', '— поправим.')}
          </p>
        </section>
      </main>

      <PublicFooter />
    </div>
  )
})

export default NotFoundPage
